import { z } from 'zod';
import type { ApiResponse, StateManagerConfig } from '../types';
import { ApiError, ValidationError } from '@lingugeek/shared';
import { NetworkError, AuthError } from '@lingugeek/errors';

// Auth handlers type
interface AuthHandlers {
  onUnauthorized?: () => void | Promise<void>;
}

// API Client class
export class ApiClient {
  private config: StateManagerConfig;
  private authHandlers: AuthHandlers = {};

  constructor(config: StateManagerConfig) {
    this.config = config;
  }

  // Set auth handlers
  setAuthHandlers(handlers: AuthHandlers) {
    this.authHandlers = handlers;
  }

  // Generic request method
  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    schema?: z.ZodType<T>
  ): Promise<ApiResponse<T>> {
    const url = `${this.config.apiBaseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 401 && this.authHandlers.onUnauthorized) {
          this.authHandlers.onUnauthorized();
        }

        // Check for authentication errors using new schema format
        const errorMessage =
          data.message || data.error || `HTTP ${response.status}`;
        const errorCode = data.code || 'API_ERROR';

        // Check for authentication-related error codes
        if (
          errorCode === 'AUTH_INVALID_TOKEN' ||
          errorCode === 'AUTH_TOKEN_EXPIRED' ||
          errorCode === 'AUTH_TOKEN_USED' ||
          errorMessage.toLowerCase().includes('token expired') ||
          errorMessage.toLowerCase().includes('invalid token') ||
          errorMessage.toLowerCase().includes('token used')
        ) {
          throw AuthError.fromMessage(errorMessage);
        }

        throw new ApiError(errorMessage, response.status, errorCode);
      }

      // Validate response if schema provided
      if (schema) {
        const validation = schema.safeParse(data.data);
        if (!validation.success) {
          console.error('Schema validation failed:', validation.error);
          console.error('Response data:', JSON.stringify(data.data, null, 2));
          throw new ValidationError(
            `Invalid response data: ${validation.error.message}`
          );
        }
        return {
          success: true,
          data: validation.data,
          status: response.status,
        };
      }

      return {
        success: true,
        data: data.data || data,
        status: response.status,
      };
    } catch (error) {
      if (
        error instanceof ApiError ||
        error instanceof AuthError ||
        error instanceof ValidationError
      ) {
        throw error;
      }

      // Network or other errors
      throw NetworkError.requestFailed(
        'unknown',
        undefined,
        {
          metadata: {
            message: error instanceof Error ? error.message : 'Request failed',
          },
        },
        error instanceof Error ? error : undefined
      );
    }
  }

  // Convenience methods
  async get<T>(endpoint: string, schema?: z.ZodType<T>): Promise<T> {
    const response = await this.request(endpoint, { method: 'GET' }, schema);
    if (!response.success || response.data === undefined) {
      throw new ApiError(
        response.error || 'Request failed',
        response.status || 500,
        'REQUEST_FAILED'
      );
    }
    return response.data;
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    schema?: z.ZodType<T>
  ): Promise<T> {
    const response = await this.request(
      endpoint,
      {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      },
      schema
    );
    if (!response.success || response.data === undefined) {
      throw new ApiError(
        response.error || 'Request failed',
        response.status || 500,
        'REQUEST_FAILED'
      );
    }
    return response.data;
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    schema?: z.ZodType<T>
  ): Promise<T> {
    const response = await this.request(
      endpoint,
      {
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      },
      schema
    );
    if (!response.success || response.data === undefined) {
      throw new ApiError(
        response.error || 'Request failed',
        response.status || 500,
        'REQUEST_FAILED'
      );
    }
    return response.data;
  }

  async delete<T>(endpoint: string, schema?: z.ZodType<T>): Promise<T> {
    const response = await this.request(endpoint, { method: 'DELETE' }, schema);
    if (!response.success || response.data === undefined) {
      throw new ApiError(
        response.error || 'Request failed',
        response.status || 500,
        'REQUEST_FAILED'
      );
    }
    return response.data;
  }
}

// Singleton instance factory
let apiClientInstance: ApiClient | null = null;

export function createApiClient(config: StateManagerConfig): ApiClient {
  if (apiClientInstance) {
    return apiClientInstance;
  }

  apiClientInstance = new ApiClient(config);
  return apiClientInstance;
}

export function getApiClient(): ApiClient {
  if (!apiClientInstance) {
    throw new Error('API Client not initialized. Call createApiClient first.');
  }
  return apiClientInstance;
}
